import {LitElement} from 'lit';
import {
  filter,
  fromEvent,
  Observable,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';

/**
 * An extension of LitElement that exposes Observable streams of various events.
 * This hooks into the LitElement lifecycle to clean up after itself,
 * and allows subclasses to forego cleaning up subscriptions based on these streams on disconnect.
 * Be careful to check all subscriptions in subclasses, if they're not from here they might need to be disposed of manually!
 */
export class RxLitElement extends LitElement {
  // #region Properties (10)

  #subscriptions: Partial<Record<keyof this, Subscription>> = {};

  subscribe<Key extends keyof this>(
    propertyKey: Key,
    stream: Observable<this[Key]>
  ) {
    this.#subscriptions[propertyKey]?.unsubscribe();
    // Sync this[propertyKey] to the stream
    this.#subscriptions[propertyKey] = stream
      .pipe(takeUntil(this.disconnect$))
      .subscribe((value) => {
        // console.log(
        //   `RxLitElement: Subscribed property: ${String(
        //     propertyKey
        //   )} on ${stream}, update to ${value}`
        // );
        this[propertyKey] = value;
        this.requestUpdate();
      });
  }

  /** A stream that, upon disconnect, emits a single value then immediately completes. */
  public disconnect$: Subject<void> = new Subject<void>();
  public contextMenu$: Observable<MouseEvent> = fromEvent<MouseEvent>(
    this,
    'contextmenu'
  ).pipe(takeUntil(this.disconnect$));
  /** A stream of all clicks on this element. */
  public click$: Observable<MouseEvent> = fromEvent<MouseEvent>(
    this,
    'click'
  ).pipe(takeUntil(this.disconnect$));
  public touchStart$: Observable<TouchEvent> = fromEvent<TouchEvent>(
    this,
    'touchstart'
  ).pipe(takeUntil(this.disconnect$));
  public touchMove$: Observable<TouchEvent> = fromEvent<TouchEvent>(
    this,
    'touchmove'
  ).pipe(takeUntil(this.disconnect$));
  public touchEnd$: Observable<TouchEvent> = fromEvent<TouchEvent>(
    this,
    'touchend'
  ).pipe(takeUntil(this.disconnect$));
  public keyDown$: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(
    document,
    'keydown'
  ).pipe(takeUntil(this.disconnect$));
  public keyPress$: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(
    document,
    'keypress'
  ).pipe(takeUntil(this.disconnect$));
  public keyUp$: Observable<KeyboardEvent> = fromEvent<KeyboardEvent>(
    document,
    'keyup'
  ).pipe(takeUntil(this.disconnect$));
  /** A stream of left clicks on this element. */
  public leftClick$: Observable<MouseEvent> = this.click$.pipe(
    filter((event) => event.button === 0)
  );
  /** A stream of middle clicks on this element. */
  public middleClick$: Observable<MouseEvent> = this.click$.pipe(
    filter((event) => event.button === 1)
  );
  /** A stream of all mouseover Events on this element. */
  public mouseOver$: Observable<MouseEvent> = fromEvent<MouseEvent>(
    this,
    'mouseover'
  ).pipe(takeUntil(this.disconnect$));
  /** A stream of all mouseup Events on this element. */
  public mouseUp$: Observable<MouseEvent> = fromEvent<MouseEvent>(
    this,
    'mouseup'
  ).pipe(takeUntil(this.disconnect$));
  /** A stream of all mousedown Events on this element. */
  public mouseDown$: Observable<MouseEvent> = fromEvent<MouseEvent>(
    this,
    'mousedown'
  ).pipe(takeUntil(this.disconnect$));
  public resize$ = fromEvent(document, 'resize').pipe(
    takeUntil(this.disconnect$)
  );
  /** A stream of right clicks on this element. */
  public rightClick$: Observable<MouseEvent> = this.click$.pipe(
    takeUntil(this.disconnect$),
    filter((event) => event.button === 2)
  );

  // #endregion Properties (10)

  // #region Public Methods (1)

  public override disconnectedCallback(): void {
    // console.log('DISCONNECT', this);
    // this.disconnect$.next();
    // this.disconnect$.complete();
    super.disconnectedCallback();
  }

  // #endregion Public Methods (1)
}
